// Updated Mock data for Contemplate Landing Page
// Theme: #202020 (dark) and #efede6 (light cream)

export const heroData = {
  headline: "Move from conflict to a peaceful, friendly relationship with your child.",
  subheadline: "Contemplate helps parents move from conflict and regret to a peaceful, friendly relationship with their children. By cultivating peace of mind within.",
  ctaText: "Become a Peaceful Parent",
  conflictImage: "https://images.unsplash.com/photo-1581579438747-1dc8d17bbce4?w=600&q=80",
  peaceImage: "https://images.unsplash.com/photo-1516627145497-ae6968895b74?w=600&q=80"
};

export const problemsData = {
  title: "You love your child — yet peace feels distant",
  problems: [
    { icon: "MessageSquareWarning", text: "Conversations escalate faster than you expect" },
    { icon: "Undo2", text: "Words come out that you wish you could take back" },
    { icon: "UserX", text: "Your child withdraws, and the silence hurts even more than the argument" },
    { icon: "Moon", text: "At night, your mind replays the day with guilt" },
    { icon: "RefreshCw", text: "You promise yourself you won't do it again" },
    { icon: "Repeat", text: "But it happens yet again. And again" }
  ],
  insight: {
    title: "You can't control yourself. You want to. But you can't.",
    content: "No parent is a bad parent. You just lack mind control. You don't need to fix your child. You need a tool to regulate your mind."
  },
  explanation: {
    title: "You don't have a problematic mind.",
    content: "The mind inherently is kind of problematic. Impatient and restless. Not your mind particularly. Everyone's mind. In its default untrained state, it chooses reacting rather than responding. Short term excitement over long term peace."
  },
  solution: {
    title: "You need to train your mind. That's about it.",
    content: "Not treat it. Not therapy. It's not a disorder. It's just mind being mind. But if you train it, then it works for you, rather than you working for it."
  }
};

export const costData = {
  title: "What is this ongoing conflict with your kid costing you?",
  costs: [
    { icon: "Sunset", text: "The peace you wish you felt at the end of the day" },
    { icon: "Heart", text: "The emotional bond you can share with your kid for years to come" },
    { icon: "Home", text: "The feeling that your home is a place of ease, not tension" },
    { icon: "Users", text: "The relationship you could have with your child when they grow up" },
    { icon: "Anchor", text: "The chance that they come to you, not pull away, when life gets hard" },
    { icon: "Camera", text: "The kind of memories you are building with them right now" }
  ],
  highlight: {
    title: "Conflict costs more than arguments.",
    content: "It costs peace of mind — slowly, quietly, every day."
  },
  conclusion: "The problem isn't that you don't love your child enough, or aren't a good parent. It's that no one taught us how to stay peaceful when emotions run high. Do you remember learning this at school?"
};

export const solutionData = {
  title: "Peace doesn't come from fixing your child — it begins with you",
  intro: "Contemplate is not about correcting behavior or giving parenting advice. It is a daily Contemplation practice that helps you:",
  benefits: [
    { icon: "Brain", text: "Think clearly when emotions rise" },
    { icon: "Heart", text: "Respond from peace instead of reaction" },
    { icon: "Volume2", text: "Speak with control, especially during difficult moments" }
  ],
  conclusion: "When peace is restored within you, relationships naturally begin to soften."
};

export const hopeData = {
  title: "Hope is not a strategy",
  content: [
    "You keep hoping it will get better tomorrow. Somehow. Magically. You hope your next interaction won't be so stressful. But nothing changes. The conflict remains.",
    "You have every intention to control your words, but you can't. You say the wrong thing because your mind controls you in that moment. Again. And again.",
    "You keep hoping it will be better next time. But hope is not a strategy."
  ],
  insight: {
    title: "You might have the intention to control it, but you don't have the training to do it.",
    content: "You need to train your mind like you train your body. You need to train your mind to be clear and peaceful."
  }
};

export const testimonialsData = [
  {
    id: 1,
    quote: "My child started opening up to me surprisingly. About their school and friends. That's never happened. Ever. I'm in the friend zone now :) Not because I forced it, but because I stopped reacting to everything. The relationship just softened. I feel lighter.",
    author: "M.",
    childAge: "parent of a 16-year-old",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80"
  },
  {
    id: 2,
    quote: "It helped me pause before speaking. And in general speak less. Because that way I don't say things I shouldn't be saying. That single change shifted the tone of our home. Not just with my kid but also with my partner. I realised what a nag I was being earlier.",
    author: "Anonymous Parent",
    childAge: "California",
    image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&q=80"
  },
  {
    id: 3,
    quote: "I didn't realize how much tension I was carrying until it began to dissolve. Because somehow the world has normalized the tension you carry as a parent. You react the way everyone else reacts. Because no one is trained to regulate their mind. My conversations with my child now feel more peaceful, and they are opening up again.",
    author: "Anonymous Parent",
    childAge: "parent of a 15-year-old",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&q=80"
  },
  {
    id: 4,
    quote: "I stopped trying to fix my child and started working on myself. I stopped meeting fire with fire and became water. And suddenly because I changed, our relationship became lighter and more friendly. No more conflict all the time. I now know that silence during critical moments is the secret key to your relationship with your kid.",
    author: "Anonymous Parent",
    childAge: "parent of a teenager",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80"
  },
  {
    id: 5,
    quote: "After Week 2, I caught myself before yelling at my child. I had never done that! I didn't even know I could do that! Like, that was even possible! That pause changed everything. We actually talked instead of fighting. I didn't give advice. I didn't correct them. We just talked. It was like a rock being lifted off my chest. Felt so peaceful.",
    author: "S.",
    childAge: "parent of a 15-year-old",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&q=80"
  }
];

export const howItWorksData = {
  title: "Would you like to train your mind to pause before reacting?",
  subtitle: "That's exactly what Contemplate teaches you.",
  clarification: "Contemplate isn't parenting advice. It's not about controlling your child's behavior. It isn't therapy. It isn't going into your past and fixing what's broken.",
  practiceTitle: "It's a daily mind fitness practice that helps you:",
  benefits: [
    { icon: "Brain", text: "Think clearly when emotions spike and not lose control" },
    { icon: "Pause", text: "Pause before saying something you'll regret" },
    { icon: "Heart", text: "Respond from peace instead of frustration" },
    { icon: "Moon", text: "End the day without guilt or replaying arguments" },
    { icon: "Users", text: "Go from butting heads to being besties with your kid" }
  ]
};

export const programData = {
  title: "Would you like to go from butting heads to being best friends with your kid?",
  subtitle: "Here's exactly how you will do it:",
  components: [
    { icon: "Calendar", title: "8-Week Structured Program", description: "2 Contemplate principles per week building on each other" },
    { icon: "Headphones", title: "Guided Contemplation Audios", description: "Regular bedtime audios for deep reflection" },
    { icon: "Compass", title: "Adioscope Decision Planner", description: "Your private decision making journal to solve real life situations" },
    { icon: "MessageCircle", title: "Speech Control Exercises", description: "Mind fitness exercises for emotional control during conversations" },
    { icon: "Target", title: "Vision Planner", description: "Make all life areas meaningful and peaceful" },
    { icon: "Sparkles", title: "Life Application", description: "Apply peace across parenting, relationships, work, and other areas" }
  ],
  timeCommitment1: "15-30 minutes per day,",
  timeCommitment2: "usually when you have your coffee",
  access: "Start Week 1 right now",
  insight: "Contemplate is a daily practice for peace of mind. It's about learning to care for your mind the way you care for your body.",
  outcome: "Each week builds on the last. By Week 8, you'll have a complete framework for responding from peace rather than reaction. Not just with your child, but in all areas of your life.",
  highlight: "When peace is restored within the parent, relationships change without force."
};

export const painPointsData = {
  title: "You'll never say this again. Ever.",
  statements: [
    "\"I'm sorry I yelled\" — for the hundredth time",
    "\"Why can't I just stay calm?\"",
    "\"Every conversation turns into a fight\"",
    "\"I lie awake replaying what I said\"",
    "\"My child has stopped talking to me\"",
    "\"I feel like I'm failing as a parent\""
  ]
};

export const pricingData = {
  question: "Is a peaceful relationship with your kid worth this much to you?",
  subtext: "What is your relationship with your child worth?",
  plan: {
    name: "Core Foundation Program",
    price: 50,
    period: "per day",
    description: "billed as $199/yr",
    features: [
      "16 Contemplate principles (2 per week)",
      "Regular guided Contemplation audios",
      "Adioscope - your private decision planner",
      "Speech control exercises",
      "Vision planner for all life areas",
      "Apply peace across parenting, work & relationships",
      "Immediate access to Week 1",
      "Cancel anytime — no contracts"
    ]
  },
  comparison: "That's less than your yearly coffee budget.",
  bottomText: ""
};

export const appScreenshots = {
  title: "Inside Contemplate",
  subtitle: "Your daily companion for peace of mind and clarity",
  screens: [
    {
      title: "Weekly Principles",
      description: "Learn 2 powerful principles each week",
      mockup: "principles"
    },
    {
      title: "Guided Contemplation",
      description: "Bedtime audios for deep reflection and peace of mind",
      mockup: "audio"
    },
    {
      title: "Adioscope Decision Planner",
      description: "Your private decision making journal to solve real life situations",
      mockup: "planner"
    }
  ]
};

export const faqData = [
  {
    question: "Tell me more about Contemplate.",
    answer: "Contemplate is a mind fitness company that enables you to think more clearly using the practice of Contemplation and a systematic framework of key mind fitness principles. This helps you think more clearly, so you can make such choices that make you more peaceful. We are currently working in multiple locations in the US and have certified Contemplate coaches at various yoga studios in Austin, Dallas, New York, and other cities."
  },
  {
    question: "Is this parenting advice?",
    answer: "No. This is a practice for your own peace of mind. We don't tell you what to say to your child or how to discipline them. We help you learn to respond from clarity and peace instead of emotion and frustration. When you change from within, relationships naturally shift."
  },
  {
    question: "Should I do this alone or along with my partner?",
    answer: "It is highly recommended that you both do it together because when you start to apply these principles, you will start to change. If your partner isn't aware of your training, they may feel that something feels a bit off. But if you both do it together if possible, you'd be on the same page. We've had couples do it together, as well as one after the other where doing it together wasn't fitting into their schedule. Some have now adopted Contemplate for their family and they practice it together with their kids each week."
  },
  {
    question: "Will my partner also see my account and what I'm working on?",
    answer: "No. All your material and the work that you do within Contemplate is private to you. When you work through your real life situations in your Adioscope, your decision planner, only you have access to it. No one else, not even people on the Contemplate team, can see what you write. It is very common for our students to work on multiple areas of their life in the Adioscope in addition to their relationship with their kids. The Contemplate approach is to make you independent so you can use the framework to find the right answers in all areas of your life."
  },
  {
    question: "When do I start?",
    answer: "Immediately. As soon as you subscribe, you get access to Week 1 and can begin your practice today. You will apply your learning in your next interaction with your kid right away."
  },
  {
    question: "How much time does this take each day?",
    answer: "About 15-30 minutes to listen to the audios and work on your Adioscope (your private decision planner). You apply the principles you learn throughout your day as situations arise. It's not time-intensive. It's integrated into your life. You are living a peaceful life. You are practising with each interaction you have at home or outside."
  },
  {
    question: "What age group does this work for?",
    answer: "The practice works for any age. It's helpful during the growing-up years when emotional conflicts tend to peak, but parents have applied this training successfully to their relationships with children of all ages, including grown-up kids."
  },
  {
    question: "What if I've tried meditation or therapy and it didn't work?",
    answer: "Contemplate isn't meditation or therapy. It's a structured practice with specific principles you apply to real-life situations. Many parents who struggled with traditional approaches have found clarity here because it's action-based, not just observation-based."
  },
  {
    question: "How is this different from meditation apps?",
    answer: "Meditation helps you observe thoughts. It doesn't give you a framework to make critical decisions. Contemplate helps you work with real situations using a framework and make better decisions in general (about love, work, parenting, finances, health, etc.) and especially in charged moments."
  },
  {
    question: "How does Contemplate fit in with my meditation practice?",
    answer: "Contemplation is the preliminary preparation for meditation practice. Once you learn to understand your mind, reduce restlessness and increase peace, you will be able to go deeper into your meditation practice. Contemplate will also have meditation training for advanced students. That will be launched in a few months."
  },
  {
    question: "Is Contemplate based on any spiritual or religious framework?",
    answer: "No. Contemplate is a non-spiritual, non-religious, simple, practical framework to think more clearly so you can make such choices that make you more peaceful. Not happy. Not excited. Not satisfied. Peaceful. You can use the Contemplate framework along with any other spiritual or religious frameworks that you follow currently."
  },
  {
    question: "What if my child is already going to therapy? Will Contemplate still help?",
    answer: "Contemplate will help you control your emotions and behavior so you stay more peaceful during your interactions with your child, and in general in all other interactions in your life (with your partner, colleagues, family, friends, etc.). We will soon also have a program for kids so your children can also go through it."
  },
  {
    question: "What if I'm already going to therapy? Will Contemplate still help?",
    answer: "Yes it will. Dr Antonio Ocana, MD, a senior psychotherapist in the US specializing in addiction, trauma, ADHD and other serious mental health conditions, and Tarun Gulati, Founder of Contemplate, discuss the scientific aspects of Contemplation, how it affects your brain and how it can be used efficiently. Dr Ocana also recommends Contemplate to his patients as necessary in addition to therapy."
  },
  {
    question: "What other programs do you have?",
    answer: "The one you will be going through is the Contemplate Foundation program. That's our core program where you learn the fundamentals and lay a strong foundation to build on. We also have specialized programs for dating, sports, business owners (values, vision, and culture), etc. that build on the Contemplation practice you learnt during the Foundation program and help you apply it to specific areas of life."
  },
  {
    question: "I'm interested in wellness and helping others. Do you have something for me?",
    answer: "Yes, we do. We have a Contemplate Coach Certification Program where you can get trained to become a certified Contemplate mind fitness coach. This is only open to the most deserving students who demonstrate a strong embodiment of the Contemplate principles during their Foundation training. You need to apply for the Certification program and you go through a selection process."
  },
  {
    question: "Can I cancel anytime?",
    answer: "Yes. There are no contracts or commitments. If at any point you feel it's not serving you, you can cancel. But we've found that the parents who commit to the 16 weeks experience the deepest shifts both in their own peace of mind as well as their relationships with their kids, partners, colleagues, friends, etc."
  },
  {
    question: "Why should I trust you?",
    answer: "You shouldn't. And you shouldn't trust anyone else either. Only trust yourself. Trust what you feel. Only trust the results you get. Try Contemplate for a few days, apply the principles and the exercises in your interactions with your kid and other people in your life, and notice if you feel anything differently. Do you start to feel more peaceful? Trust yourself. Trust what you feel. Trust the results. Contemplate is simple, but not easy. It will take some work on your part. The question again is, what is your relationship with your kid worth to you? What is the current conflict costing you? Are you willing to work on it to make it better, or take the easy road out and blame the world, social media, phones, schools, and everyone else?"
  }
];

export const finalCtaData = {
  title: "Let's change something today so your tomorrow is different", //Tomorrow Will Be Like Today If You Don't Change Something Today
  painPoints: [
    "\"I'm sorry I yelled\" for the hundredth time",
    "\"Why can't I just stay calm?\"",
    "\"Every conversation turns into a fight\"",
    "\"I lie awake replaying what I said\"",
    "\"My child has stopped talking to me\"",
    "\"I feel like I'm failing as a parent\""
  ],
  highlight: "Parents have already changed their relationships with their kids by changing themselves first.",
  ctaText: "Become a Peaceful Parent",
  disclaimer: [
    "Start to improve your relationship with your kid in the next few hours.",
    "This isn't magic. This isn't hope. This isn't therapy.",
    "This is mind fitness training. By Contemplate."
  ]
};

export const contemplateFrameworkData = {
  title: "The Contemplate Framework",
  subtitle: "Adios Principles",
  description: "Contemplation on the following principles of Adios helps you improve your clarity of thought, which enables you to make such decisions that increase your peace of mind.",
  principles: [
    {
      number: "01",
      title: "Twin Voices",
      description: "Sometimes in certain situations, you hear two or more than two voices inside your head. You need to be able to clearly identify and differentiate between these voices and ask yourself - pursuing which voice will make me more peaceful in the long run?"
    },
    {
      number: "02",
      title: "The Urge to Prove",
      description: "Do you experience an urge to prove that you are right, or someone else is wrong in your personal, professional or social interactions? If you give in to that urge, does that make you feel more peaceful or more restless?"
    },
    {
      number: "03",
      title: "The Urge to Share",
      description: "Do you experience an impatience to share things with others in person or online the moment you learn them? Does that make you feel more peaceful or more restless?"
    },
    {
      number: "04",
      title: "Negative Comparison",
      description: "Negative comparison with others that makes you feel inferior usually happens because of lack of internal clarity. Use Contemplation to gain and retain internal clarity so you focus on what makes you feel more fulfilled, purposeful and peaceful."
    },
    {
      number: "05",
      title: "Right vs Wrong",
      description: "Whatever makes you more peaceful is right for you. Whatever makes you less peaceful or more restless is wrong for you."
    },
    {
      number: "06",
      title: "Feels good vs feels right",
      description: "If something feels good in the moment, does it also feel peaceful in the long run? If something feels bad in the moment, does it feel peaceful in the long run?"
    },
    {
      number: "07",
      title: "Negative thoughts",
      description: "Consider negative thoughts to be normal. Everyone experiences them. Instead of feeling bad or guilty about them, consider them to be dust gathered on your mind. Focus on wiping the dust off."
    },
    {
      number: "08",
      title: "Negative Speech",
      description: "Remove all kinds of negative speech from your routine. It usually makes you less peaceful."
    },
    {
      number: "09",
      title: "Peace of Mind",
      description: "Peace of mind is what remains after you remove everything that makes you restless. Find the elements that make you restless, remove them, and add elements that make you more peaceful."
    },
    {
      number: "10",
      title: "Quantity of Speech",
      description: "You must speak less throughout the day. More silence helps you create the right environment for Contemplation."
    },
    {
      number: "11",
      title: "Velocity and Pitch of Speech",
      description: "When you speak, speak at a moderate velocity and pitch. Too fast or too slow, or too high or too low, can make you more restless."
    },
    {
      number: "12",
      title: "Looking inward vs outward",
      description: "You must focus on looking inward and remaining connected to yourself as you go through your day. Focusing too much on the outside, on external validation, appreciation or attention usually keeps you more restless."
    },
    {
      number: "13",
      title: "Breaking up your day",
      description: "Break up your day in smaller sections of 3-4 hours each and aim to get that section to be peaceful. Review after each section and make corrections if necessary for the next section of the day."
    },
    {
      number: "14",
      title: "Define your next version",
      description: "Plan your life in terms of your Adios year and not the calendar year. Your Adios year runs from your birthday to birthday. Break up the year into quarters and months ending on the same date of the relevant month. This makes it much more personal. Define the next version of yourself - the version you want to be by your next birthday."
    },
    {
      number: "15",
      title: "Internal search engine",
      description: "Use your internal search engine to find answers to critical questions in your life. Train it well and use it often to get results efficiently."
    },
    {
      number: "16",
      title: "Form vs Substance",
      description: "You should be able to differentiate between appearance and substance in various situations you might come across. Don't focus too much on form while ignoring substance. Strong substance sets a strong foundation. Only good appearance with weak substance may not make you very peaceful in the long run."
    }
  ]
};